import {
  Box,
  CircularProgress,
  Drawer,
  Typography,
  Divider,
  IconButton,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useState } from "react";
import { formatCurrency } from "@/utils/format-currency";
import { useComplaint } from "@/hooks/use-complaint";
import { resolveComplaint } from "@/services/disputes.service";
import { showToast } from "@/redux/actions/toast-actions";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { CustomButton } from "@/components/custom/custom-button";
import { DisputeStatusChip } from "./dispute-status-chip";
import { DisputeReasonDialog } from "./dispute-reason-dialog";
import { COMPLAINT_STATUS, COMPLAINT_TYPE_LABEL } from "../../data";

const DrawerContent = styled(Box)(({ theme }) => ({
  width: "33vw",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  overflowY: "auto",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  paddingBlock: theme.spacing(1.2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-of-type": { borderBottom: "none" },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: theme.palette.text.secondary,
  flex: 1,
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  color: theme.palette.text.primary,
  flex: 2,
  textAlign: "right",
  wordBreak: "break-word",
}));

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    return format(new Date(dateStr), "MMM d, yyyy h:mm a");
  } catch {
    return dateStr;
  }
};

export function ComplaintSheet({ complaint: rowComplaint, open, onClose }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const { data: fetchedComplaint, isLoading } = useComplaint(
    open ? rowComplaint?.merchantComplaintId : undefined,
  );
  const complaint = fetchedComplaint ?? rowComplaint;
  const id = complaint?.merchantComplaintId;

  const resolveMutation = useMutation({
    mutationFn: ({ verdict, note }) => resolveComplaint({ id, verdict, note }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["complaints"] });
      queryClient.invalidateQueries({ queryKey: ["complaint", id] });
      queryClient.invalidateQueries({ queryKey: ["complaintStats"] });
      dispatch(showToast(res?.message ?? "Complaint updated", "success"));
    },
    onError: (error) => {
      dispatch(showToast(error?.message ?? "Could not update complaint", "error"));
    },
  });

  const canResolve = complaint?.disputeStatus === "OPEN";
  const isDecided = Boolean(complaint?.disputeStatus) && !canResolve;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      SlotProps={{ backdrop: { onClick: onClose } }}
    >
      <DrawerContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Complaint Details
              {isLoading && <CircularProgress size={14} thickness={5} />}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Ref: {id ?? "N/A"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {COMPLAINT_TYPE_LABEL[complaint?.disputeType] ?? complaint?.disputeType ?? "Complaint"}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {complaint?.disputeAmount ? formatCurrency(complaint.disputeAmount) : "—"}
            </Typography>
          </Box>
          <DisputeStatusChip status={complaint?.disputeStatus} config={COMPLAINT_STATUS} />
        </Box>

        <SectionTitle>Merchant</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Merchant Name</InfoLabel>
            <InfoValue>{complaint?.merchantName ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Merchant Email</InfoLabel>
            <InfoValue>{complaint?.merchantEmail ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Date Submitted</InfoLabel>
            <InfoValue>{formatDate(complaint?.dateCreated)}</InfoValue>
          </InfoRow>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Transaction</SectionTitle>
        <Box sx={{ mb: 3 }}>
          <InfoRow>
            <InfoLabel>Tracking Number</InfoLabel>
            <InfoValue>{complaint?.trackingNumber ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Date</InfoLabel>
            <InfoValue>{complaint?.transactionDate ?? "N/A"}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Transaction Charge</InfoLabel>
            <InfoValue>
              {complaint?.transactionCharge ? formatCurrency(complaint.transactionCharge) : "N/A"}
            </InfoValue>
          </InfoRow>
          {complaint?.files && (
            <InfoRow>
              <InfoLabel>Evidence</InfoLabel>
              <InfoValue>
                <a href={complaint.files} target="_blank" rel="noopener noreferrer">
                  View file
                </a>
              </InfoValue>
            </InfoRow>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        <SectionTitle>Complaint Details</SectionTitle>
        <Typography variant="body2" sx={{ mb: 3 }}>
          {complaint?.reasonForDisputeInDetails ?? "N/A"}
        </Typography>

        {isDecided && (
          <>
            <Divider sx={{ mb: 3 }} />
            <SectionTitle>Resolution</SectionTitle>
            <Box sx={{ mb: 3 }}>
              <DisputeStatusChip status={complaint?.disputeStatus} config={COMPLAINT_STATUS} />
              {complaint?.adminNote && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  {complaint.adminNote}
                </Typography>
              )}
            </Box>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Divider sx={{ mb: 2 }} />

        {canResolve && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <CustomButton variantType="destructive" onClick={() => setRejectDialogOpen(true)}>
              Reject
            </CustomButton>
            <CustomButton variantType="default" onClick={() => setResolveDialogOpen(true)}>
              Resolve
            </CustomButton>
          </Box>
        )}
      </DrawerContent>

      <CustomConfirmDialog
        open={resolveDialogOpen}
        onClose={() => setResolveDialogOpen(false)}
        title="Resolve Complaint"
        description="Mark this complaint as resolved. The merchant will see this update on their dashboard."
        confirmLabel="Confirm Resolve"
        mutation={resolveMutation}
        onConfirm={() => resolveMutation.mutate({ verdict: "RESOLVED" })}
      />

      <DisputeReasonDialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        title="Reject Complaint"
        description="Let the merchant know why this complaint is being declined."
        reasonLabel="Reason for rejection"
        reasonPlaceholder="e.g. Deduction matches the agreed fee schedule..."
        confirmLabel="Confirm Reject"
        mutation={resolveMutation}
        onConfirm={(reason) => resolveMutation.mutate({ verdict: "REJECTED", note: reason })}
      />
    </Drawer>
  );
}
